import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, UploadSimple, EnvelopeSimple, FloppyDisk, SealCheck } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/DashboardLayout";
import { PageHeader, Spinner } from "../components/ui-bits";
import { api, getMerchantId } from "../lib/api";

export default function Settings() {
  const merchantId = getMerchantId();
  const [merchant, setMerchant] = useState(null);
  const [color, setColor] = useState("#1B2B5E");
  const [email, setEmail] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/merchants/${merchantId}`);
        setMerchant(data);
        if (data.brand_color) setColor(data.brand_color);
        if (data.merchant_email) setEmail(data.merchant_email);
        if (data.logo_url) setLogoPreview(data.logo_url);
      } catch {
        toast.error("Failed to load settings");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_000_000) return toast.error("Logo must be under 2MB");
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("brand_color", color);
      if (email) fd.append("merchant_email", email);
      if (logoFile) fd.append("logo", logoFile);
      const { data } = await api.post(`/merchants/${merchantId}/branding`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.branding.logoUrl) setLogoPreview(data.branding.logoUrl);
      toast.success("Branding saved — your verification pages are updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader overline="Store Identity" title="Settings" subtitle="Brand your public verification pages and set the email used for owner notifications." />

      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        {/* form */}
        <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-7" data-testid="settings-form">
          <div>
            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50 flex items-center gap-1.5"><UploadSimple size={13} /> Store Logo</label>
            <div className="mt-3 flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl bg-cream border border-navy/10 flex items-center justify-center overflow-hidden">
                {logoPreview ? <img src={logoPreview} alt="logo" className="w-full h-full object-contain" /> : <Palette size={22} className="text-navy/20" />}
              </div>
              <label className="cursor-pointer text-sm text-navy border border-navy/15 rounded-full px-4 py-2 hover:border-gold hover:text-gold transition-colors">
                Choose file
                <input type="file" accept="image/*" onChange={onPickLogo} className="hidden" data-testid="settings-logo-input" />
              </label>
            </div>
            <p className="text-[0.68rem] text-navy/40 mt-2">PNG or JPG, up to 2MB. Shown on your public verification pages.</p>
          </div>

          <div className="mt-7">
            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50 flex items-center gap-1.5"><Palette size={13} /> Cover Colour</label>
            <div className="mt-3 flex items-center gap-3">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-11 w-14 rounded-lg border border-navy/10 bg-white cursor-pointer" data-testid="settings-color-input" />
              <input value={color} onChange={(e) => setColor(e.target.value)} className="flex-1 bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy font-mono" />
            </div>
          </div>

          <div className="mt-7">
            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50 flex items-center gap-1.5"><EnvelopeSimple size={13} /> Support / Notification Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="support@yourbrand.com" className="mt-3 w-full bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy" data-testid="settings-email-input" />
            <p className="text-[0.68rem] text-navy/40 mt-2">Used as the owner address for warranty reminders when items are still merchant-owned.</p>
          </div>

          <button onClick={save} disabled={saving} data-testid="settings-save-button" className="mt-8 w-full flex items-center justify-center gap-2 bg-navy text-cream rounded-xl py-4 text-sm hover:brightness-110 active:scale-[0.99] transition-all disabled:opacity-60">
            {saving ? <><Spinner className="h-4 w-4" /> Saving…</> : <><FloppyDisk size={17} weight="light" /> Save Branding</>}
          </button>
        </div>

        {/* live preview */}
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50 mb-3">Verification Page Preview</p>
          <div className="rounded-2xl overflow-hidden border border-navy/10 shadow-luxe">
            <div style={{ backgroundColor: color }} className="px-5 py-4 flex items-center justify-between">
              {logoPreview ? (
                <img src={logoPreview} alt="logo" className="h-8 w-auto max-w-[130px] object-contain" />
              ) : (
                <span className="font-playfair text-lg text-cream">{merchant?.merchant_name || "Your Store"}</span>
              )}
              <span className="flex items-center gap-1.5 text-[0.58rem] uppercase tracking-[0.2em] text-white bg-white/15 rounded-full px-2.5 py-1"><SealCheck size={12} weight="fill" /> Verified</span>
            </div>
            <div className="bg-cream p-6">
              <div className="bg-white rounded-xl p-5 shadow-luxe">
                <span className="text-[0.55rem] uppercase tracking-[0.18em] text-navy/50 bg-navy/5 rounded-full px-2 py-0.5">Watch</span>
                <h3 className="font-playfair text-xl text-navy mt-2">Celeste Automatic Chronograph</h3>
                <p className="text-xs text-navy/45">Valura Horology</p>
              </div>
              <p className="text-center text-navy/40 text-[0.68rem] mt-4">Powered by <span className="font-playfair text-navy">Vcoin</span></p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
